import { ICSElement } from '../types/interfaces';
import { 
  IElementInspector, 
  ElementInspectionInfo, 
  StructureSnapshot, 
  SerializationOptions, 
  SerializationResult 
} from '../types/devtools-interfaces';

/**
 * Инспектор элементов CSElement
 * Предоставляет инструменты для анализа структуры и сериализации
 */
export class ElementInspector implements IElementInspector {
  private static readonly VERSION = '1.0.0';

  /**
   * Получить детальную информацию об элементе
   */
  inspect(element: ICSElement): ElementInspectionInfo {
    // Приводим к CSElement для доступа к приватным полям
    const csElement = element as any;
    const children: string[] = Array.from(csElement._elements?.keys() || []);
    const parent = this.findParent(element);
    const depth = this.calculateDepth(element);
    const path = this.getElementPath(element);

    return {
      id: element.id,
      type: element.constructor.name,
      data: this.extractData(element),
      children,
      parent: parent?.id,
      depth,
      path,
      metadata: {
        created: this.getCreationTime(element),
        modified: this.getModificationTime(element),
        size: this.calculateSize(element),
        childCount: children.length,
        isRoot: parent === null
      }
    };
  }

  /**
   * Создать снимок всей структуры начиная с корневого элемента
   */
  createSnapshot(rootElement: ICSElement): StructureSnapshot {
    const timestamp = Date.now();
    const elements = new Map<string, ElementInspectionInfo>();
    const parentChild = new Map<string, string[]>();
    const childParent = new Map<string, string>();

    // Рекурсивно обходим всю структуру
    this.traverseStructure(rootElement, (element) => {
      const info = this.inspect(element);
      elements.set(element.id, info);

      // Строим карты отношений
      if (info.children.length > 0) {
        parentChild.set(element.id, info.children);
        info.children.forEach(childId => {
          childParent.set(childId, element.id);
        });
      }
    });

    // Вычисляем статистику
    const statistics = {
      totalElements: elements.size,
      maxDepth: Math.max(...Array.from(elements.values()).map(e => e.depth)),
      memoryUsage: this.estimateMemoryUsage(elements)
    };

    return {
      timestamp,
      rootId: rootElement.id,
      elements,
      relationships: {
        parentChild,
        childParent
      },
      statistics
    };
  }

  /**
   * Сериализовать элемент в JSON-совместимый формат
   */
  serialize(element: ICSElement, options: SerializationOptions = {}): SerializationResult {
    const {
      includePrivateData = false,
      maxDepth = Infinity,
      excludeFields = [],
      includeMetadata = true,
      format = 'json'
    } = options;

    let elementCount = 0;

    const serializeElement = (el: ICSElement, currentDepth: number): any => {
      elementCount++;

      if (currentDepth >= maxDepth) {
        return { id: el.id, type: 'truncated' };
      }

      const data: any = {
        id: el.id,
        type: el.constructor.name
      };

      // Сериализуем данные элемента
      const elementData = this.extractData(el, includePrivateData);
      Object.keys(elementData).forEach(key => {
        if (!excludeFields.includes(key)) {
          data[key] = elementData[key];
        }
      });

      // Сериализуем дочерние элементы
      const csEl = el as any;
      if (csEl._elements?.size > 0) {
        data.children = {};
        csEl._elements.forEach((child: any, key: string) => {
          data.children[key] = serializeElement(child, currentDepth + 1);
        });
      }

      // Добавляем метаданные если требуется
      if (includeMetadata) {
        data._metadata = {
          depth: currentDepth,
          childCount: csEl._elements?.size || 0,
          serializedAt: Date.now()
        };
      }

      return data;
    };

    let serializedData = serializeElement(element, 0);

    // Применяем форматирование
    if (format === 'compact') {
      serializedData = this.compactFormat(serializedData);
    } else if (format === 'debug') {
      serializedData = this.debugFormat(serializedData);
    }

    const serializedString = JSON.stringify(serializedData);
    const serializedSize = new Blob([serializedString]).size;

    return {
      data: serializedData,
      metadata: {
        version: ElementInspector.VERSION,
        timestamp: Date.now(),
        elementCount,
        serializedSize
      }
    };
  }

  /**
   * Десериализовать элемент из данных
   */
  deserialize(data: any): ICSElement {
    if (!data || typeof data !== 'object') {
      throw new Error('Некорректные данные для десериализации');
    }

    // Импортируем CSElement динамически чтобы избежать циклических зависимостей
    const { CSElement } = require('../core/CSElement');

    const deserializeElement = (elementData: any): ICSElement => {
      const element = new CSElement(elementData.id);

      // Восстанавливаем данные элемента
      Object.keys(elementData).forEach(key => {
        if (key !== 'id' && key !== 'type' && key !== 'children' && key !== '_metadata') {
          try {
            element.setData(key, elementData[key]);
          } catch (error) {
            console.warn(`Не удалось восстановить поле ${key}:`, error);
          }
        }
      });

      // Восстанавливаем дочерние элементы
      if (elementData.children) {
        Object.entries(elementData.children).forEach(([key, childData]: [string, any]) => {
          const childElement = deserializeElement(childData);
          element.addElement(childElement, key);
        });
      }

      return element;
    };

    return deserializeElement(data);
  }

  /**
   * Найти элемент по пути от корневого элемента
   */
  findByPath(rootElement: ICSElement, path: string[]): ICSElement | null {
    let current = rootElement;

    for (const segment of path) {
      const csElement = current as any;
      const child = csElement._elements?.get(segment);
      if (!child) {
        return null;
      }
      current = child;
    }

    return current;
  }

  /**
   * Получить путь от корня до элемента
   */
  getElementPath(element: ICSElement): string[] {
    const path: string[] = [];
    let current: ICSElement | null = element;

    while (current) {
      const parent = this.findParent(current);
      if (parent) {
        // Найти ключ, по которому текущий элемент хранится в родителе
        const csParent = parent as any;
        for (const [key, child] of csParent._elements || []) {
          if (child === current) {
            path.unshift(key);
            break;
          }
        }
        current = parent;
      } else {
        break;
      }
    }

    return path;
  }

  /**
   * Найти родительский элемент
   */
  private findParent(_element: ICSElement): ICSElement | null {
    // Поскольку CSElement не хранит ссылку на родителя,
    // нужно искать среди всех элементов (это может быть оптимизировано)
    // В реальной реализации стоит добавить parent ссылку в CSElement
    return null; // Временная заглушка
  }

  /**
   * Вычислить глубину элемента в структуре
   */
  private calculateDepth(element: ICSElement): number {
    let depth = 0;
    let current = element;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const parent = this.findParent(current);
      if (!parent) break;
      depth++;
      current = parent;
    }

    return depth;
  }

  /**
   * Извлечь данные элемента
   */
  private extractData(element: ICSElement, includePrivate: boolean = false): Record<string, any> {
    const data: Record<string, any> = {};

    // Извлекаем публичные данные
    element.data.forEach((value, key) => {
      if (includePrivate || !key.startsWith('_')) {
        try {
          // Пытаемся сериализовать значение
          JSON.stringify(value);
          data[key] = value;
        } catch {
          // Если значение не сериализуется, записываем его тип
          data[key] = `[${typeof value}]`;
        }
      }
    });

    return data;
  }

  /**
   * Получить время создания элемента
   */
  private getCreationTime(element: ICSElement): number {
    return element.data.get('_created') || Date.now();
  }

  /**
   * Получить время последней модификации
   */
  private getModificationTime(element: ICSElement): number {
    return element.data.get('_modified') || Date.now();
  }

  /**
   * Вычислить размер элемента в байтах (приблизительно)
   */
  private calculateSize(element: ICSElement): number {
    try {
      const serialized = JSON.stringify(this.extractData(element, true));
      return new Blob([serialized]).size;
    } catch {
      return 0;
    }
  }

  /**
   * Рекурсивно обойти всю структуру
   */
  private traverseStructure(element: ICSElement, callback: (el: ICSElement) => void): void {
    callback(element);
    const csElement = element as any;
    csElement._elements?.forEach((child: ICSElement) => {
      this.traverseStructure(child, callback);
    });
  }

  /**
   * Оценить использование памяти
   */
  private estimateMemoryUsage(elements: Map<string, ElementInspectionInfo>): number {
    let totalSize = 0;
    elements.forEach(info => {
      totalSize += info.metadata.size;
    });
    return totalSize;
  }

  /**
   * Компактный формат сериализации
   */
  private compactFormat(data: any): any {
    // Удаляем метаданные и лишние поля для экономии места
    const compact = (obj: any): any => {
      if (Array.isArray(obj)) {
        return obj.map(compact);
      } else if (obj && typeof obj === 'object') {
        const result: any = {};
        Object.keys(obj).forEach(key => {
          if (!key.startsWith('_') && key !== 'type') {
            result[key] = compact(obj[key]);
          }
        });
        return result;
      }
      return obj;
    };

    return compact(data);
  }

  /**
   * Отладочный формат сериализации
   */
  private debugFormat(data: any): any {
    // Добавляем дополнительную отладочную информацию
    const addDebugInfo = (obj: any, path: string[] = []): any => {
      if (Array.isArray(obj)) {
        return obj.map((item, index) => addDebugInfo(item, [...path, index.toString()]));
      } else if (obj && typeof obj === 'object') {
        const result: any = { ...obj };
        result._debug = {
          path: path.join('.'),
          keys: Object.keys(obj).length,
          hasChildren: !!obj.children
        };
        
        if (obj.children) {
          result.children = {};
          Object.entries(obj.children).forEach(([key, value]) => {
            result.children[key] = addDebugInfo(value, [...path, key]);
          });
        }
        
        return result;
      }
      return obj;
    };

    return addDebugInfo(data);
  }
} 